/**
 * Centralized UTM Manager
 *
 * Single source of truth for UTM capture, storage, and retrieval.
 * Call captureUtmsFromUrl() once in main.tsx before React mounts.
 * All components read UTMs via getUtms() or one of the fallback presets.
 */

import { getStorage } from '../utils/general.util';

// ── Types ──────────────────────────────────────────────────────────

export interface UtmData {
	utm_source: string | null;
	utm_medium: string | null;
	utm_campaign: string | null;
	utm_term: string | null;
	utm_content: string | null;
}

type UtmKey = keyof UtmData;

/** The 3 keys persisted to the user profile / checkout payload. */
type AttributionKey = 'utm_source' | 'utm_medium' | 'utm_campaign';
type AttributionUtms = Record<AttributionKey, string>;

/** Profile payload: only the UTMs actually captured from the URL.
 *  Absent keys are omitted so the backend never overwrites a stored
 *  origin with a fabricated placeholder. */
type ProfileUtms = Partial<Record<UtmKey, string>>;

// ── Storage keys (single definition; param list derives from it) ────

const STORAGE_KEYS: Record<UtmKey, string> = {
	utm_source: 'utm_source',
	utm_medium: 'utm_medium',
	utm_campaign: 'utm_campaign',
	utm_term: 'utm_term',
	utm_content: 'utm_content',
};

const OVERWRITE_KEY = 'overwrite_utm';

// Derived from STORAGE_KEYS so the param list and the storage-key map can
// never drift. Object insertion order is preserved.
const UTM_PARAMS = Object.keys(STORAGE_KEYS) as UtmKey[];

// ── Storage helpers (delegate to centralized getStorage) ───────────

function storageGet(key: string): string | null {
	try {
		return getStorage().getItem(key);
	} catch {
		return null;
	}
}

function storageSet(key: string, value: string): void {
	try {
		getStorage().setItem(key, value);
	} catch {
		// Storage unavailable — silently fail
	}
}

// ── Core: persist + capture ────────────────────────────────────────

// Applies the overwrite policy to a batch of incoming UTMs:
//   overwrite_utm unset → store all + mark 'true' (first visit)
//   = 'true'            → last-touch: overwrite all
//   = 'false'           → first-touch: only fill empty slots
// Shared by captureUtmsFromUrl (URL) and setUtms (internal nav).
function persistUtms(incoming: Partial<UtmData>): void {
	const overwrite = storageGet(OVERWRITE_KEY);
	if (overwrite === null) storageSet(OVERWRITE_KEY, 'true');
	const overwriteAll = overwrite === null || overwrite === 'true';
	for (const key of UTM_PARAMS) {
		const val = incoming[key];
		if (val == null) continue;
		if (overwriteAll || !storageGet(STORAGE_KEYS[key])) {
			storageSet(STORAGE_KEYS[key], val);
		}
	}
}

/**
 * Reads UTMs from the current URL and persists them.
 * Must be called ONCE in main.tsx, before ReactDOM.createRoot().
 */
export function captureUtmsFromUrl(): void {
	const params = new URLSearchParams(window.location.search);
	const incoming: Partial<UtmData> = {};
	for (const key of UTM_PARAMS) {
		const val = params.get(key);
		if (val) incoming[key] = val;
	}
	// No UTMs in the URL → don't touch storage or the overwrite flag.
	if (Object.keys(incoming).length === 0) return;
	persistUtms(incoming);
}

// ── Getters ────────────────────────────────────────────────────────

/** Returns all UTM values from storage. Null if not set. Derived from
 *  STORAGE_KEYS so adding a key here is automatic. */
export function getUtms(): UtmData {
	const result = {} as UtmData;
	for (const key of UTM_PARAMS) {
		result[key] = storageGet(STORAGE_KEYS[key]);
	}
	return result;
}

// One fallback engine for the 3-key attribution payloads. `fallback` is
// either a single string applied to every key, or a per-key map. Every
// "give me UTMs with a guaranteed value" consumer goes through this so the
// fallback policy lives in exactly one place.
function getAttributionUtms(
	fallback: string | AttributionUtms,
): AttributionUtms {
	const stored = getUtms();
	const resolve = (key: AttributionKey) =>
		stored[key] ?? (typeof fallback === 'string' ? fallback : fallback[key]);
	return {
		utm_source: resolve('utm_source'),
		utm_medium: resolve('utm_medium'),
		utm_campaign: resolve('utm_campaign'),
	};
}

/** UTMs for checkout `additional_information.utms` ('organic' fallback). */
export function getUtmsForCheckout(): AttributionUtms {
	return getAttributionUtms('organic');
}

/** UTMs for patchUserProfileService: only the values actually captured.
 *  Absent keys are omitted (no fabricated fallback). When there's no
 *  utm_source but an ad click ID is present, derive the real source from
 *  it (e.g. fbclid -> facebook) so the platform isn't lost as NULL. */
export function getUtmsForProfile(): ProfileUtms {
	const stored = getUtms();
	const result: ProfileUtms = {};
	for (const key of UTM_PARAMS) {
		const val = stored[key];
		if (val) result[key] = val;
	}
	if (!result.utm_source) {
		const derived = deriveSourceFromClickId();
		if (derived) result.utm_source = derived;
	}
	return result;
}

/** UTMs for analytics events ('empty' fallback). */
export function getUtmsForTracking(): AttributionUtms {
	return getAttributionUtms('empty');
}

// ── Setters ────────────────────────────────────────────────────────

/**
 * Set UTMs respecting the overwrite policy.
 * Used by banners/sidebar when navigating to /plans with PLANS_UTM_VALUES.
 */
export function setUtms(utms: Partial<UtmData>): void {
	persistUtms(utms);
}

// ── Click IDs (ad-platform click identifiers) ──────────────────────
// Unlike UTMs (free-text labels under an overwrite policy), a click ID is
// minted by the ad platform for one specific click. The one that matters is
// the click that brought the user on this visit, so we keep the latest.

const CLICK_ID_PARAMS = [
	'gclid', 'gbraid', 'wbraid', // Google
	'fbclid', // Meta
	'ttclid', // TikTok
	'rdt_cid', // Reddit
	'msclkid', // Microsoft
] as const;

type ClickIdKey = (typeof CLICK_ID_PARAMS)[number];

/**
 * Reads ad click IDs from the current URL and persists the latest value.
 * Call ONCE in main.tsx, right after captureUtmsFromUrl().
 */
export function captureClickIdsFromUrl(): void {
	const params = new URLSearchParams(window.location.search);
	for (const key of CLICK_ID_PARAMS) {
		const val = params.get(key);
		if (val) storageSet(key, val);
	}
}

/** Stored click IDs as a flat object — only the keys that ever arrived. */
export function getClickIds(): Partial<Record<ClickIdKey, string>> {
	const result: Partial<Record<ClickIdKey, string>> = {};
	for (const key of CLICK_ID_PARAMS) {
		const val = storageGet(key);
		if (val) result[key] = val;
	}
	return result;
}

// The utm_source implied by each ad-platform click ID.
const CLICK_ID_SOURCE: Record<ClickIdKey, string> = {
	gclid: 'google', gbraid: 'google', wbraid: 'google',
	fbclid: 'facebook', ttclid: 'tiktok', rdt_cid: 'reddit', msclkid: 'bing',
};

/** Real utm_source implied by the first stored click ID, else null.
 *  Used only when no utm_source was captured — never fabricates a value. */
function deriveSourceFromClickId(): string | null {
	for (const key of CLICK_ID_PARAMS) {
		if (storageGet(key)) return CLICK_ID_SOURCE[key];
	}
	return null;
}
